import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { SentinelExample } from "../../model_gen/sentinel";
import { Sentinel } from "../../model_gen/sentinel";
import { SentinelService } from "../../services/SentinelService";
import { usePermissions } from "../../PermissionsProvider";
import { SeverityLevel } from "../../types/ISentinels";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Separator } from "../ui/separator";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { 
  RotateCcwIcon, 
  PlusIcon, 
  MinusIcon,
  Loader2Icon,
  InfoIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Tooltip } from "../ui/tooltip";

interface SentinelFormProps {
  initialSentinel?: Sentinel;
  loading: boolean;
  setLoading?: (loading: boolean) => void;
  onSubmit: (formData: any) => void;
  onClose: () => void;
  submitButtonText: string;
  readOnly?: boolean;
  regenerationEnabled?: boolean;
  intentGroupId?: number;
}

interface SentinelGenerationExamples {
  id: number;
  exampleType: string;
  inputExample: string;
}

const SentinelForm: React.FC<SentinelFormProps> = ({
  initialSentinel,
  loading,
  setLoading,
  onSubmit,
  onClose,
  submitButtonText,
  readOnly = false,
  regenerationEnabled = false,
  intentGroupId,
}) => {
  const { apiKey } = usePermissions();
  const [sentinelName, setSentinelName] = useState(
    initialSentinel?.sentinel_name || "",
  );
  const [directive, setDirective] = useState(
    initialSentinel?.sentinel_directive?.directive_body?.main || "",
  );
  const [examples, setExamples] = useState<SentinelGenerationExamples[]>(
    initialSentinel?.sentinel_directive?.examples?.map(
      (example: SentinelExample, index: number) => ({
        id: index,
        exampleType: example.example_type,
        inputExample: example.example_body,
      }),
    ) || [],
  );
  const [severity, setSeverity] = useState(
    initialSentinel?.severity || SeverityLevel.INFO,
  );
  const [includeInput, setIncludeInput] = useState(
    initialSentinel?.meta?.include_input || "",
  );
  const [includeContext, setIncludeContext] = useState(
    initialSentinel?.meta?.input_include_context || "",
  );
  const [includeSchema, setIncludeSchema] = useState(
    initialSentinel?.meta?.input_include_schema || "",
  );
  const [includeSystemPrompt, setIncludeSystemPrompt] = useState(
    initialSentinel?.meta?.input_include_system || "",
  );
  const [includeToolCalls, setIncludeToolCalls] = useState(
    initialSentinel?.meta?.include_tool_calls || "",
  );
  const [qualificationType, setQualificationType] = useState(
    initialSentinel?.meta?.qualification_type || "",
  );
  const [qualificationDirective, setQualificationDirective] = useState(
    initialSentinel?.meta?.qualification_directive || "",
  );
  const [correctionType, setCorrectionType] = useState(
    initialSentinel?.meta?.correction_type || "",
  );
  const [correctionDirective, setCorrectionDirective] = useState(
    initialSentinel?.meta?.correction_directive || "",
  );



  const exampleIdCounter = useRef(
    initialSentinel?.sentinel_directive?.examples?.length || 0,
  );

  useEffect(() => {
    // Update form state when initialSentinel changes
    setSentinelName(initialSentinel?.sentinel_name || "");
    setDirective(
      initialSentinel?.sentinel_directive?.directive_body?.main || "",
    );
    setExamples(
      initialSentinel?.sentinel_directive?.examples?.map(
        (example: SentinelExample, index: number) => ({
          id: index,
          exampleType: example.example_type,
          inputExample: example.example_body,
        }),
      ) || [],
    );
    setSeverity(initialSentinel?.severity || SeverityLevel.INFO);
    setIncludeInput(initialSentinel?.meta?.include_input || "");
    setIncludeContext(initialSentinel?.meta?.input_include_context || "");
    setIncludeSchema(initialSentinel?.meta?.input_include_schema || "");
    setIncludeSystemPrompt(initialSentinel?.meta?.input_include_system || "");
    setIncludeToolCalls(initialSentinel?.meta?.include_tool_calls || "");
    setQualificationType(initialSentinel?.meta?.qualification_type || "");
    setQualificationDirective(
      initialSentinel?.meta?.qualification_directive || "",
    );
    setCorrectionType(initialSentinel?.meta?.correction_type || "");
    setCorrectionDirective(initialSentinel?.meta?.correction_directive || "");
  }, [initialSentinel]);

  const handleAddExample = useCallback(() => {
    setExamples((prev) => [
      ...prev,
      { id: exampleIdCounter.current++, exampleType: "", inputExample: "" },
    ]);
  }, []);

  const handleRemoveExample = useCallback((itemId: number) => {
    setExamples((prev) => prev.filter((entry) => entry.id !== itemId));
  }, []);

  const handleExampleTypeChange = useCallback(
    (itemId: number, newType: string) => {
      setExamples((prev) =>
        prev.map((entry) =>
          entry.id === itemId ? { ...entry, exampleType: newType } : entry,
        ),
      );
    },
    [],
  );

  const handleInputExampleChange = useCallback(
    (itemId: number, newInput: string) => {
      setExamples((prev) =>
        prev.map((entry) =>
          entry.id === itemId ? { ...entry, inputExample: newInput } : entry,
        ),
      );
    },
    [],
  );

  const handleSubmit = () => {
    const updatedSentinel: Sentinel = {
      id: initialSentinel?.id || 0,
      sentinel_name: sentinelName,
      date_created: initialSentinel?.date_created || 0,
      sentinel_directive: {
        id: initialSentinel?.sentinel_directive?.id || 0,
        sentinel_id: initialSentinel?.sentinel_directive?.sentinel_id || 0,
        date_created: initialSentinel?.sentinel_directive?.date_created || 0,
        directive_body: {
          main: directive,
          override:
            initialSentinel?.sentinel_directive?.directive_body?.override || "",
        },
        examples: examples.map((example) => ({
          example_type: example.exampleType,
          example_body: example.inputExample,
        })),
      },
      state: initialSentinel?.state || "",
      severity: severity,
      meta: {
        ...initialSentinel?.meta,
        include_input: includeInput,
        input_include_context: includeContext,
        input_include_system: includeSystemPrompt,
        input_include_schema: includeSchema,
        include_tool_calls: includeToolCalls,
        qualification_type: qualificationType,
        qualification_directive: qualificationDirective,
        correction_type: correctionType,
        correction_directive: correctionDirective,
      },
      sentinel_generation_id: initialSentinel?.sentinel_generation_id || 0,
      qa: initialSentinel?.qa || [],
      intent_group_id: intentGroupId || initialSentinel?.intent_group_id,
    };

    onSubmit(updatedSentinel);
  };

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [activeRegenerateFunction, setActiveRegenerateFunction] = useState<
    ((feedback: string) => void) | null
  >(null);
  const [activeSection, setActiveSection] = useState<
    "Qualification" | "Evaluation" | "Correction" | "Sentinel" | null
  >(null);
  const [expectationText, setExpectationText] = useState("");
  const [conditionText, setConditionText] = useState("");

  const handleQualificationRegenerate = useCallback(() => {
    setActiveSection("Qualification");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinelQualification(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setQualificationType(
            regeneratedSentinel.meta?.qualification_type || "",
          );
          setQualificationDirective(
            regeneratedSentinel.meta?.qualification_directive || "",
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating qualification:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey, expectationText, conditionText]);

  const handleEvaluationRegenerate = useCallback(() => {
    setActiveSection("Evaluation");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinelEvaluation(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setDirective(
            regeneratedSentinel.sentinel_directive?.directive_body?.main || "",
          );
          setExamples(
            regeneratedSentinel.sentinel_directive?.examples?.map(
              (example: SentinelExample, index: number) => ({
                id: index,
                exampleType: example.example_type,
                inputExample: example.example_body,
              }),
            ) || [],
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating evaluation:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey]);

  const handleCorrectionRegenerate = useCallback(() => {
    setActiveSection("Correction");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinelCorrection(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setCorrectionType(regeneratedSentinel.meta?.correction_type || "");
          setCorrectionDirective(
            regeneratedSentinel.meta?.correction_directive || "",
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating correction:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey]);

  const handleSentinelRegenerate = useCallback(() => {
    setActiveSection("Sentinel");
    setActiveRegenerateFunction(() => (feedback: string) => {
      if (!initialSentinel?.id) return;

      const payload: any = { feedback };
      if (!initialSentinel?.meta?.expectation && expectationText) {
        payload.expectation = expectationText;
      }
      if (!initialSentinel?.meta?.condition && conditionText) {
        payload.condition = conditionText;
      }

      setLoading?.(true);
      SentinelService.regenerateSentinel(
        initialSentinel.id,
        payload,
        null,
        apiKey,
      ).subscribe(
        (regeneratedSentinel: Sentinel) => {
          if (!initialSentinel?.meta?.expectation) {
            initialSentinel.meta.expectation = expectationText;
          }
          if (!initialSentinel?.meta?.condition) {
            initialSentinel.meta.condition = conditionText;
          }
          setSentinelName(regeneratedSentinel.sentinel_name || "");
          setDirective(
            regeneratedSentinel.sentinel_directive?.directive_body?.main || "",
          );
          setExamples(
            regeneratedSentinel.sentinel_directive?.examples?.map(
              (example: SentinelExample, index: number) => ({
                id: index,
                exampleType: example.example_type,
                inputExample: example.example_body,
              }),
            ) || [],
          );
          setSeverity(regeneratedSentinel.severity || SeverityLevel.INFO);
          setQualificationType(
            regeneratedSentinel.meta?.qualification_type || "",
          );
          setQualificationDirective(
            regeneratedSentinel.meta?.qualification_directive || "",
          );
          setCorrectionType(regeneratedSentinel.meta?.correction_type || "");
          setCorrectionDirective(
            regeneratedSentinel.meta?.correction_directive || "",
          );
          setLoading?.(false);
        },
        (error: any) => {
          console.error("Error regenerating sentinel:", error);
          setLoading?.(false);
        },
      );
    });
    setIsFeedbackModalOpen(true);
  }, [initialSentinel?.id, setLoading, apiKey]);

  const handleModalSubmit = () => {
    if (activeRegenerateFunction) {
      activeRegenerateFunction(feedbackText);
    }
    setFeedbackText("");
    setExpectationText("");
    setConditionText("");
    setIsFeedbackModalOpen(false);
    setActiveRegenerateFunction(null);
    setActiveSection(null);
  };

  const handleModalCancel = () => {
    setFeedbackText("");
    setExpectationText("");
    setConditionText("");
    setIsFeedbackModalOpen(false);
    setActiveRegenerateFunction(null);
    setActiveSection(null);
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-gc-med text-foreground">Name</Label>
            <span className="text-red-500">*</span>
            <Tooltip content="Your name for this Sentinel">
              <InfoIcon className="w-4 h-4 text-muted-foreground" />
            </Tooltip>
          </div>
          <Input
            value={sentinelName}
            onChange={(e) => setSentinelName(e.target.value)}
            placeholder="Menu Monitor"
            disabled={loading || readOnly}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-gc-med text-foreground">Severity</Label>
            <span className="text-red-500">*</span>
            <Tooltip content="Select the severity level for this Sentinel">
              <InfoIcon className="w-4 h-4 text-muted-foreground" />
            </Tooltip>
          </div>
          <Select
            value={severity.toString()}
            onValueChange={(value) => setSeverity(parseInt(value))}
            disabled={loading || readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select severity" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SeverityLevel)
                .filter(
                  ([key]) =>
                    !isNaN(
                      Number(SeverityLevel[key as keyof typeof SeverityLevel]),
                    ),
                )
                .map(([key]) => (
                  <SelectItem
                    key={SeverityLevel[key as keyof typeof SeverityLevel]}
                    value={SeverityLevel[key as keyof typeof SeverityLevel].toString()}
                  >
                    {key}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="space-y-6">
        {/* Qualification Section */}
        {(qualificationType || qualificationDirective) ? (
          <Collapsible defaultOpen={false}>
            <Card className="border-border">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between w-full">
                    <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                      Qualification
                    </CardTitle>
                    {regenerationEnabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQualificationRegenerate();
                        }}
                        disabled={loading}
                        className="gap-2 mr-6"
                      >
                        {loading ? (
                          <Loader2Icon className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcwIcon className="w-4 h-4" />
                        )}
                        Regenerate
                      </Button>
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">Type</Label>
                      <span className="text-red-500">*</span>
                      <Tooltip content="Select the type of qualification">
                        <InfoIcon className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Select
                      value={qualificationType as string}
                      onValueChange={setQualificationType}
                      disabled={loading || readOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STATIC">STATIC</SelectItem>
                        <SelectItem value="LLM">LLM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">Directive</Label>
                      <span className="text-red-500">*</span>
                      <Tooltip content="Tells the sentinel how to qualify the input">
                        <InfoIcon className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Textarea
                      value={qualificationDirective as string}
                      onChange={(e) => setQualificationDirective(e.target.value)}
                      placeholder="Specify how to qualify the input"
                      rows={4}
                      disabled={loading || readOnly}
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ) : (
          regenerationEnabled && (
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-gc-med text-foreground">Qualification</h3>
                    <p className="text-sm text-muted-foreground">Generate qualification logic for this sentinel</p>
                  </div>
                  <Button
                    onClick={handleQualificationRegenerate}
                    disabled={loading}
                    className="gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* Evaluation Section */}
        <Collapsible defaultOpen={false}>
          <Card className="border-border">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between w-full">
                  <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                    Evaluation
                  </CardTitle>
                  {regenerationEnabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEvaluationRegenerate();
                      }}
                      disabled={loading}
                      className="gap-2 mr-6"
                    >
                      {loading ? (
                        <Loader2Icon className="w-4 h-4 animate-spin" />
                      ) : (
                        <RotateCcwIcon className="w-4 h-4" />
                      )}
                      Regenerate
                    </Button>
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">Directive</Label>
                    <span className="text-red-500">*</span>
                    <Tooltip content="Tells the sentinel what to monitor for">
                      <InfoIcon className="w-4 h-4 text-muted-foreground" />
                    </Tooltip>
                  </div>
                  <Textarea
                    value={directive}
                    onChange={(e) => setDirective(e.target.value)}
                    placeholder="Make sure the customer doesn't order items that aren't on the menu"
                    rows={4}
                    disabled={loading || readOnly}
                  />
                </div>

                {/* Examples Section */}
                <Collapsible defaultOpen={false}>
                  <Card className="border-border bg-card/50">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between w-full">
                          <CardTitle className="text-base font-gc-med text-foreground">
                            Examples
                          </CardTitle>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="space-y-4">
                        {examples.map((item) => (
                          <div key={item.id} className="flex items-start gap-4">
                            <div className="w-24">
                              <Select
                                value={item.exampleType}
                                onValueChange={(value) => handleExampleTypeChange(item.id, value)}
                                disabled={loading || readOnly}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PASS">
                                    <span className="text-green-600">PASS</span>
                                  </SelectItem>
                                  <SelectItem value="FAULT">
                                    <span className="text-red-600">FAULT</span>
                                  </SelectItem>
                                  <SelectItem value="NA">
                                    <span className="text-muted-foreground">NA</span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Textarea
                              value={item.inputExample}
                              onChange={(e) => handleInputExampleChange(item.id, e.target.value)}
                              placeholder="Example"
                              rows={4}
                              disabled={loading || readOnly}
                              className="flex-1"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveExample(item.id)}
                              disabled={loading || readOnly}
                              className="mt-1"
                            >
                              <MinusIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          onClick={handleAddExample}
                          disabled={loading || readOnly}
                          className="w-full gap-2 border-dashed"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Example
                        </Button>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Correction Section */}
        {(correctionType || correctionDirective) ? (
          <Collapsible defaultOpen={false}>
            <Card className="border-border">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between w-full">
                    <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                      Correction
                    </CardTitle>
                    {regenerationEnabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCorrectionRegenerate();
                        }}
                        disabled={loading}
                        className="gap-2 mr-6"
                      >
                        {loading ? (
                          <Loader2Icon className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcwIcon className="w-4 h-4" />
                        )}
                        Regenerate
                      </Button>
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">Type</Label>
                      <span className="text-red-500">*</span>
                      <Tooltip content="Select the type of correction">
                        <InfoIcon className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Select
                      value={correctionType as string}
                      onValueChange={setCorrectionType}
                      disabled={loading || readOnly}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STATIC">STATIC</SelectItem>
                        <SelectItem value="LLM">LLM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">Directive</Label>
                      <span className="text-red-500">*</span>
                      <Tooltip content="Tells the sentinel how to correct the input">
                        <InfoIcon className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Textarea
                      value={correctionDirective as string}
                      onChange={(e) => setCorrectionDirective(e.target.value)}
                      placeholder="Specify how to correct the input"
                      rows={4}
                      disabled={loading || readOnly}
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ) : (
          regenerationEnabled && (
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-gc-med text-foreground">Correction</h3>
                    <p className="text-sm text-muted-foreground">Generate correction logic for this sentinel</p>
                  </div>
                  <Button
                    onClick={handleCorrectionRegenerate}
                    disabled={loading}
                    className="gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        )}
      </div>

      {/* Include Options */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-gc-med text-foreground">Include Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-gc-med text-foreground">System Prompt</Label>
              <Switch
                checked={includeSystemPrompt === "true"}
                onCheckedChange={(checked) => setIncludeSystemPrompt(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-gc-med text-foreground">Context</Label>
              <Switch
                checked={includeContext === "true"}
                onCheckedChange={(checked) => setIncludeContext(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-gc-med text-foreground">Input</Label>
              <Switch
                checked={includeInput === "true"}
                onCheckedChange={(checked) => setIncludeInput(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-gc-med text-foreground">Tool Calls</Label>
              <Switch
                checked={includeToolCalls === "true"}
                onCheckedChange={(checked) => setIncludeToolCalls(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-gc-med text-foreground">JSON Schema</Label>
              <Switch
                checked={includeSchema === "true"}
                onCheckedChange={(checked) => setIncludeSchema(checked ? "true" : "")}
                disabled={loading || readOnly}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {!readOnly && (
        <div className="flex items-center gap-3 pt-6 border-t border-border">
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="gap-2"
          >
            {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            {submitButtonText}
          </Button>
          {regenerationEnabled && (
            <Button
              variant="outline"
              onClick={handleSentinelRegenerate}
              disabled={loading}
              className="gap-2"
            >
              {loading ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcwIcon className="w-4 h-4" />
              )}
              Regenerate All
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </div>
      )}

      {/* Feedback Modal */}
      <Dialog open={isFeedbackModalOpen} onOpenChange={setIsFeedbackModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{activeSection ? `Regenerate ${activeSection}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Feedback</Label>
              <p className="text-xs text-muted-foreground">
                Provides user feedback to be considered during the regeneration operations
              </p>
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Enter your feedback here..."
                rows={4}
              />
            </div>

            {!initialSentinel?.meta?.expectation && (
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">Expectation</Label>
                <p className="text-xs text-muted-foreground">
                  Define the expected behavior
                </p>
                <Textarea
                  value={expectationText}
                  onChange={(e) => setExpectationText(e.target.value)}
                  placeholder="Enter the expected behavior..."
                  rows={4}
                />
              </div>
            )}

            {!initialSentinel?.meta?.condition && (
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">Condition</Label>
                <p className="text-xs text-muted-foreground">
                  Define the condition for this sentinel
                </p>
                <Textarea
                  value={conditionText}
                  onChange={(e) => setConditionText(e.target.value)}
                  placeholder="Enter the condition..."
                  rows={4}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleModalCancel}>Cancel</Button>
            <Button onClick={handleModalSubmit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SentinelForm;
